/**
 * @file PageSwitcherRow.tsx
 * @description Строка переключателя страниц и экранов (phase19.16 Р1-6а;
 * Р3а — uiux-r3 §3.1 «Главная ✓ на сайте · О нас ● правки · Акции ◌ скрыта ·
 * Новая ✦ ещё не была на сайте»): название, строка под ним и значок статуса
 * (значок + слово — не только цвет). Высота от 48 (касание).
 */

import { Check, Circle, CircleDashed, Sparkles, Trash2, type LucideIcon } from "lucide-react";

import type { PageStatusKey } from "../../../lib/site-builder/page-status";
import { cn } from "@/lib/utils";

/** Значок статуса (реестр — новый статус записью). */
const STATUS_ICON: Readonly<Record<PageStatusKey, { icon: LucideIcon; className: string }>> = {
  live: { icon: Check, className: "text-success" },
  changes: { icon: Circle, className: "fill-current text-warning" },
  hidden: { icon: CircleDashed, className: "text-on-surface-variant" },
  never: { icon: Sparkles, className: "text-info" },
  deleted: { icon: Trash2, className: "text-destructive" },
};

interface Props {
  title: string;
  meta: string;
  active: boolean;
  onClick: () => void;
  status?: PageStatusKey;
  /** Точка «есть правки» у скрытой страницы (статус «скрыта», но правки ждут). */
  dot?: boolean;
}

export function PageSwitcherRow({ title, meta, active, onClick, status, dot = false }: Props) {
  const icon = status ? STATUS_ICON[status] : null;
  const Icon = icon?.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? "page" : undefined}
      className={cn(
        "flex min-h-12 w-full items-center gap-2 rounded-lg px-3 py-1.5 text-left outline-none focus-visible:ring-2 focus-visible:ring-ring",
        active ? "bg-primary/10" : "hover:bg-surface-low",
      )}
    >
      <span className="flex min-w-0 flex-1 flex-col justify-center">
        <span className="type-body flex min-w-0 items-center gap-1.5 font-medium text-on-surface">
          <span className="truncate" title={title}>{title}</span>
          {dot ? <Circle aria-label="есть правки" className="size-2 shrink-0 fill-current text-warning" /> : null}
        </span>
        <span className="type-caption truncate text-on-surface-variant">{meta}</span>
      </span>
      {Icon && icon ? <Icon aria-hidden className={cn("size-4 shrink-0", icon.className)} /> : null}
    </button>
  );
}
